import React from 'react';
import { useTranslation } from 'react-i18next';
import TranslationNamespaces from '../../locales/TranslationNamespaces';
import { QInputForm } from '@kubiq-base/core';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAppContext } from '../../state/AppContext';
import { useCache } from '../../state/CachingContext';
import { useApi } from '../../state/ApiContext';
import { ITokensExternal } from '../../interfaces/';
import SiteHelper from '../../helpers/SiteHelper';
import CenteredQButton from './CenteredQButton';
import HeaderWithDescription from './HeaderWithDescription';
import FlexFormContainer from './FlexContainers/FlexFormContainer';
import FlexContainer from './FlexContainers/FlexContainer';

declare global {
  interface Window {
    ReactNativeWebView: { postMessage: (arg0: string) => void };
  }
}

interface Props {
  isPasswordReset?: boolean;
  internalRedirectTo?: string;
}

const Enter2FACode = ({ isPasswordReset, internalRedirectTo }: Props): JSX.Element => {
  const namespace = TranslationNamespaces.twoFA;
  const { t } = useTranslation(namespace);
  const { postRequest } = useApi();
  const { setEmail, setTokens } = useAppContext();
  const { getCache, setCache, clearCache } = useCache();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<{ code: string }>();

  const [isInRecovery, setIsInRecovery] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const navigate = useNavigate();
  const session = getCache();

  const isExternalLogin = session && !session.invitationguid && (session.my || session.organisationsapplicationsguid);

  const length = isInRecovery ? 8 : 6;
  const textName = isInRecovery ? 'RecoveryCode' : 'Code';
  const errorText = `invalid2FA${textName}`;

  const validateCode = (value: string): string | undefined => {
    setErrorMessage(undefined);
    let error;

    if ((!isInRecovery && !/^[0-9]{6,6}$/i.test(value)) || (isInRecovery && !/^[0-9A-Z]{8,8}$/i.test(value))) {
      error = errorText;
    }

    return error;
  };

  const getPostContext = (code: string): { url: string; postData: unknown } => {
    let controller = 'TwoFactorAuthentication';
    let url = `${controller}/VerifyIDPLogin`;
    // let url = isInRecovery ? `${controller}/VerifyIDPLogin` : `${controller}/VerifyExternalLogin`;

    if (isPasswordReset) {
      // Reset password, so first step verify based on email
      controller = 'ResetPassword';
      url = isInRecovery ? `${controller}/VerifyRecoveryCode` : `${controller}/Verify`;
      return { url, postData: { verificationCode: code, email: session.email } };
    } else if (isExternalLogin) {
      url = isInRecovery ? `${controller}/VerifyRecoveryCodeExternalLogin` : `${controller}/VerifyExternalLogin`;

      return {
        url,
        postData: {
          verificationCode: code,
          authenticationType: 'web',
          ...(session.organisationsapplicationsguid && {
            organisationApplicationGuid: session.organisationsapplicationsguid,
          }),
          ...(session.my && { my: true }),
          requestId: session.requestid,
        },
      };
    } else {
      return {
        url,
        postData: {
          verificationCode: code,
          authenticationType: 'web',
        },
      };
    }
  };

  const onSubmit = async (data: { code: string }): Promise<void> => {
    if (data.code) {
      setIsSubmitting(true);
      const postContext = getPostContext(data.code);

      await postRequest<{ data: ITokensExternal }>(postContext.url, postContext.postData, undefined, [
        'codeExpired',
        'invalidCode',
        'TODOSUPPRESSRECOVERYCODE',
      ])
        .then(async (result) => {
          if (result.data) {
            if (internalRedirectTo) {
              if (result.data.tokens) {
                setTokens({
                  accessToken: result.data.tokens.accessToken,
                  refreshToken: result.data.tokens.refreshToken,
                });
              } else {
                setTokens({
                  accessToken: result.data.accessToken || '',
                  refreshToken: result.data.refreshToken || '',
                });
              }

              setCache('authData', result.data);
              navigate(internalRedirectTo);
            } else {
              setTokens(result.data.tokens);
              SiteHelper.handleRedirectExternal(getCache(), result.data, setEmail, clearCache, setCache, navigate);
            }
          }
        })
        .catch(() => {
          setIsSubmitting(false);
          setErrorMessage(`enter${textName}FailedMessage`);
        });
    } else {
      setErrorMessage(`enter${textName}FailedMessage`);
    }
  };

  return (
    <>
      <HeaderWithDescription bigHeader description={`enter${textName}Description`} translationNamespace={namespace} />
      <FlexFormContainer onSubmit={handleSubmit(onSubmit)}>
        <QInputForm
          control={control}
          name='code'
          label={isInRecovery ? t('recoveryCode') : t('code')}
          autoFocus={true}
          rules={{
            validate: validateCode,
            required: 'requiredField',
            minLength: { value: length, message: errorText },
            maxLength: { value: length, message: errorText },
          }}
          isError={(errors.code && t(errors.code?.message as string)) || (errorMessage && t(errorMessage))}
        />

        <CenteredQButton type='submit' isLoading={isSubmitting} fullWidth color='secondary'>
          {t(isInRecovery ? 'recover' : 'verify')}
        </CenteredQButton>

        {isInRecovery && session.verifiedPhone && (
          <CenteredQButton to={isPasswordReset ? '/Recovery/DeadEnd' : '/Recovery/2FA'} variant='text' fullWidth>
            {t('unableToAuthenticate')}
          </CenteredQButton>
        )}
      </FlexFormContainer>

      <FlexContainer>
        <CenteredQButton
          variant='text'
          onClick={() => {
            setErrorMessage(undefined);
            reset();
            setIsInRecovery(!isInRecovery);
          }}
        >
          {t(isInRecovery ? 'previous' : 'goToRecoveryCode')}
        </CenteredQButton>

        <CenteredQButton variant='text' to='/'>
          {t('cancel')}
        </CenteredQButton>
      </FlexContainer>
    </>
  );
};

export default Enter2FACode;
